import { Injectable, OnDestroy } from '@angular/core';
import { MandantConfig } from '@strategies';
import { BehaviorSubject } from 'rxjs';

export interface Color {
  name: string;
  hex: string;
  darkContrast: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class DynamicThemeService implements OnDestroy {
  primaryColor = '#bb0000';

  accentColor = '#FF0000';

  primaryColorPalette: Color[] = [];

  accentColorPalette: Color[] = [];

  public brandingLogo$ = new BehaviorSubject('');

  public brandingFavicon$ = new BehaviorSubject('');

  public brandingTitle$ = new BehaviorSubject('Smart Portal');

  public backgroundUrl$ = new BehaviorSubject('./assets/images/polygon.svg');

  public backgroundColor$ = new BehaviorSubject('#AAA');

  ngOnDestroy() {
    this.brandingLogo$.unsubscribe();
    this.brandingFavicon$.unsubscribe();
    this.brandingTitle$.unsubscribe();
    this.backgroundUrl$.unsubscribe();
  }

  public async loadTheme(mandant: MandantConfig) {
    this.setTheme(mandant.primary, mandant.accent);
    this.brandingLogo$.next(mandant.logo);
    this.brandingFavicon$.next(mandant.faviconUrl || mandant.logo);
    this.brandingTitle$.next(mandant.brandingTitle || 'Smart Portal if no brandingtitle');
    this.backgroundUrl$.next(mandant.backgroundUrl || './assets/images/polygon.svg');
    this.backgroundColor$.next(mandant.backgroundColor || mandant.accent);
    if (mandant.customCssUrl) {
      this.setCustomCss(mandant.customCssUrl);
    }
  }

  public setTheme(primaryColor: string, accentColor?: string) {
    this.setPrimaryColor(primaryColor);
    this.setAccentColor(accentColor || primaryColor);
  }

  public setPrimaryColor(colorHexString: string) {
    this.primaryColorPalette = this.computeColors(colorHexString);
    this.updateTheme(this.primaryColorPalette, 'primary');
  }

  public setAccentColor(colorHexString: string) {
    this.accentColorPalette = this.computeColors(colorHexString);
    this.updateTheme(this.accentColorPalette, 'accent');
  }

  private updateTheme(colors: Color[], theme: string) {
    colors.forEach(color => {
      document.documentElement.style.setProperty(`--theme-${theme}-${color.name}`, color.hex);
      document.documentElement.style.setProperty(
        `--theme-${theme}-contrast-${color.name}`,
        color.darkContrast ? 'rgba(black, 0.87)' : 'white'
      );
    });
  }

  private computeColors(hex: string): Color[] {
    return [
      this.getColorObject(this.hslToHex(this.lighten(this.hexToHsl(hex), 52)), '50'),
      this.getColorObject(this.hslToHex(this.lighten(this.hexToHsl(hex), 37)), '100'),
      this.getColorObject(this.hslToHex(this.lighten(this.hexToHsl(hex), 26)), '200'),
      this.getColorObject(this.hslToHex(this.lighten(this.hexToHsl(hex), 12)), '300'),
      this.getColorObject(this.hslToHex(this.lighten(this.hexToHsl(hex), 6)), '400'),
      this.getColorObject(this.hslToHex(this.hexToHsl(hex)), '500'),
      this.getColorObject(this.hslToHex(this.darken(this.hexToHsl(hex), 6)), '600'),
      this.getColorObject(this.hslToHex(this.darken(this.hexToHsl(hex), 12)), '700'),
      this.getColorObject(this.hslToHex(this.darken(this.hexToHsl(hex), 18)), '800'),
      this.getColorObject(this.hslToHex(this.darken(this.hexToHsl(hex), 24)), '900'),
      this.getColorObject(
        this.hslToHex(this.saturate(this.lighten(this.hexToHsl(hex), 50), 30)),
        'A100'
      ),
      this.getColorObject(
        this.hslToHex(this.saturate(this.lighten(this.hexToHsl(hex), 30), 30)),
        'A200'
      ),
      this.getColorObject(
        this.hslToHex(this.saturate(this.lighten(this.hexToHsl(hex), 10), 15)),
        'A400'
      ),
      this.getColorObject(
        this.hslToHex(this.saturate(this.lighten(this.hexToHsl(hex), 5), 5)),
        'A700'
      ),
    ];
  }

  private hexToHsl(hex: string): { h: number; s: number; l: number } {
    hex = hex.replace(/^#/, '');
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map(c => c + c)
        .join('');
    }

    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h *= 60;
    }

    return { h, s: s * 100, l: l * 100 };
  }

  private lighten(hsl: { h: number; s: number; l: number }, amount: number) {
    return { ...hsl, l: Math.min(hsl.l + amount, 100) };
  }

  private darken(hsl: { h: number; s: number; l: number }, amount: number) {
    return { ...hsl, l: Math.max(hsl.l - amount, 0) };
  }

  private saturate(hsl: { h: number; s: number; l: number }, amount: number) {
    return { ...hsl, s: Math.min(hsl.s + amount, 100) };
  }

  private hslToHex(hsl: { h: number; s: number; l: number }): string {
    const h = hsl.h;
    const s = hsl.s / 100;
    const l = hsl.l / 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    let r = 0,
      g = 0,
      b = 0;

    if (0 <= h && h < 60) [r, g, b] = [c, x, 0];
    else if (60 <= h && h < 120) [r, g, b] = [x, c, 0];
    else if (120 <= h && h < 180) [r, g, b] = [0, c, x];
    else if (180 <= h && h < 240) [r, g, b] = [0, x, c];
    else if (240 <= h && h < 300) [r, g, b] = [x, 0, c];
    else if (300 <= h && h < 360) [r, g, b] = [c, 0, x];

    const toHex = (n: number) =>
      Math.round((n + m) * 255)
        .toString(16)
        .padStart(2, '0');

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  private getColorObject(value, name: string): Color {
    return {
      name,
      hex: value,
      darkContrast: false,
    };
  }

  private setCustomCss(cssUrl) {
    let link = document.getElementById('customCss');
    if (!link) {
      link = document.createElement('link');
      link.id = 'customCss';
      link.setAttribute('rel', 'stylesheet');
      link.setAttribute('href', cssUrl);
      document.head.appendChild(link);
    } else {
      link.setAttribute('rel', 'stylesheet');
      link.setAttribute('href', cssUrl);
    }
  }
}
